import Accordion from 'components/Accordion';
import ApplicationActivityList from 'components/ApplicationActivityList';
import DetailsViewCardHeader from 'components/DetailsViewCardHeader';
import DetailsViewEntityMenu from 'components/DetailsViewEntityMenu';
import DetailsViewPageContainer from 'components/DetailsViewPageContainer';
import LinkedEntitiesAccordion from 'components/LinkedEntitiesAccordion';
import UpdateApplicationForm from 'components/forms/UpdateApplicationForm';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import FlightPlannerPanel from './components/FlightPlannerPanel';
import JohnDeereExportModal from './components/JohnDeereExportModal';
import {
    CARD_HEADER_DETAIL_TYPE,
    DETAIL_TYPE_BATTERY,
    DETAIL_TYPE_DRONE,
    DETAIL_TYPE_PILOT,
    DETAIL_TYPE_SCHEDULED_PILOT,
    TEST_ID
} from './constants';
import type { FunctionComponent, Props } from './types';

const ApplicationDetailsPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        batteryMenuItems,
        boundary,
        currentApplication,
        droneMenuItems,
        dropdownMenuItems,
        entitiesLink,
        flights,
        geometry,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleExportModalClose,
        handleMapComplete,
        handleNextFlightsPage,
        handlePathPlanClose,
        handlePathPlanSaved,
        handlePlannedFlightPathsChange,
        handlePlannerSessionIdChange,
        handlePrescriptionChange,
        handleSelectFlight,
        handleSendToDrone,
        handleToggleActivity,
        handleToggleLinkedEntities,
        hasMoreFlights,
        isActivityOpen,
        isApplicationLoading,
        isClosing,
        isEditingForm,
        isExportModalOpen,
        isLinkedEntitiesOpen,
        isPlanning,
        isSendingToDrone,
        mapFlightPaths,
        mapInteractionOptions,
        mapOptions,
        pilotMenuItems,
        prescriptionBounds,
        prescriptionZones,
        scheduledPilotMenuItems,
        selectedFlight,
        sendToDroneResultText,
        setIsClosing,
        telemetryPointsByFlightId
    } = useData();

    const { ACTIVITY, LINKED_ENTITIES } = useTranslation();

    const { hasDrawingControls } = mapInteractionOptions;

    return (
        <DetailsViewPageContainer
            flights={flights}
            geometry={geometry}
            handleClose={handleClose}
            handleEditMapClick={handleEditMapClick}
            handleEditMapClose={handleEditMapClose}
            handleMapComplete={handleMapComplete}
            hasDrawingControls={hasDrawingControls}
            mapFlightPaths={mapFlightPaths}
            mapInteractionOptions={mapInteractionOptions}
            mapOptions={mapOptions}
            prescriptionBounds={prescriptionBounds}
            prescriptionZones={prescriptionZones}
            selectedFlight={selectedFlight}
            telemetryPointsByFlightId={telemetryPointsByFlightId}
            testId={testId}>
            <DetailsViewCardHeader
                detailType={CARD_HEADER_DETAIL_TYPE}
                isLoading={isApplicationLoading}
                items={dropdownMenuItems}
                name={currentApplication?.name ?? ''}
            />

            {isPlanning && currentApplication && boundary ? (
                <FlightPlannerPanel
                    application={currentApplication}
                    boundary={boundary}
                    isSendingToDrone={isSendingToDrone}
                    onClose={handlePathPlanClose}
                    onFlightPathsChange={handlePlannedFlightPathsChange}
                    onPrescriptionChange={handlePrescriptionChange}
                    onSaved={handlePathPlanSaved}
                    onSendToDrone={handleSendToDrone}
                    onSessionIdChange={handlePlannerSessionIdChange}
                    sendToDroneResultText={sendToDroneResultText}
                />
            ) : isEditingForm ? (
                <UpdateApplicationForm
                    formValues={currentApplication}
                    isClosing={isClosing}
                    onClose={handleEditClose}
                    setIsClosing={setIsClosing}
                />
            ) : (
                <>
                    <LinkedEntitiesAccordion
                        entitiesLink={entitiesLink}
                        isOpen={isLinkedEntitiesOpen}
                        label={LINKED_ENTITIES}
                        onToggle={handleToggleLinkedEntities}>
                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_DRONE}
                            isLoading={isApplicationLoading}
                            items={droneMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_BATTERY}
                            isLoading={isApplicationLoading}
                            items={batteryMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_SCHEDULED_PILOT}
                            isLoading={isApplicationLoading}
                            items={scheduledPilotMenuItems}
                        />

                        <DetailsViewEntityMenu
                            entityType={DETAIL_TYPE_PILOT}
                            isLoading={isApplicationLoading}
                            items={pilotMenuItems}
                        />
                    </LinkedEntitiesAccordion>

                    <Accordion
                        isOpen={isActivityOpen}
                        label={ACTIVITY}
                        onToggle={handleToggleActivity}>
                        <ApplicationActivityList
                            applicationStatus={currentApplication?.status}
                            fetchNextPage={handleNextFlightsPage}
                            flights={flights}
                            hasNextPage={hasMoreFlights}
                            isLoading={isApplicationLoading}
                            onSelectFlight={handleSelectFlight}
                            selectedFlightId={selectedFlight?.id}
                            telemetryPointsByFlightId={telemetryPointsByFlightId}
                        />
                    </Accordion>
                </>
            )}

            {currentApplication && boundary && (
                <JohnDeereExportModal
                    application={currentApplication}
                    boundary={boundary}
                    flights={flights}
                    isOpen={isExportModalOpen}
                    onClose={handleExportModalClose}
                    telemetryPointsByFlightId={telemetryPointsByFlightId}
                />
            )}
        </DetailsViewPageContainer>
    );
};

export default ApplicationDetailsPage;
